import { useState } from 'react';
import styled from 'styled-components';

import { useMutation } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import DatePicker from 'components/shared/form/DatePicker';
import InputGroup from 'components/shared/form/InputGroup';
import StandardModal, { Actions, PatientName } from 'components/shared/StandardModal';
import { useRehabStates } from 'context/rehabStates';
import { useTranslation } from 'context/translations';
import { simpleDashDate } from 'lib/date';
import { RehabStateApiName } from 'models/RehabState';
import { createLocationEpisodeRehabState } from 'services/api/locationEpisodeRehabStates';

const FormContainer = styled.div`
  padding-bottom: 20px;
  display: flex;
  justify-content: center;
`;

export type AdmittedAtModalType = {
  setShow: (b: boolean) => void;
  invalidateData: () => void;
  locationEpisodeId: string;
  patientName: string;
  title?: string;
};

export default function AdmittedAtModal(props: AdmittedAtModalType) {
  const { setShow, locationEpisodeId, patientName, invalidateData, title } = props;

  const { mutate, isPending } = useMutation({
    mutationFn: createLocationEpisodeRehabState,
  });

  const rehabStates = useRehabStates();
  const { translate } = useTranslation();

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [dateError, setDateError] = useState('');
  const convdate = simpleDashDate(startDate);

  const cancel = () => {
    setShow(false);
    setStartDate(null);
    setDateError('');
  };
  const submit = () => {
    if (!convdate) return;

    mutate(
      {
        locationEpisodeId,
        enteredAt: convdate,
        rehabStateId: rehabStates!.find((x) => x.apiName === RehabStateApiName.InTreatment)!.id,
      },
      {
        onSuccess: () => {
          invalidateData();
          cancel();
        },
      }
    );
  };

  return (
    <StandardModal title={title ?? `Confirm ${translate('Admission')} Date`} onCancel={cancel}>
      <PatientName>{patientName}</PatientName>
      <FormContainer>
        <InputGroup title={`Actual ${translate('Admission')} Date`} error={dateError} $containerWidth='auto'>
          <DatePicker
            $hasError={!!dateError}
            maxDate={new Date()}
            selected={startDate}
            onChange={(z) => {
              setDateError(z ? '' : 'This field is required');
              setStartDate(z);
            }}
          />
        </InputGroup>
      </FormContainer>
      <Actions>
        <Button variant='ghost' onClick={cancel}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={!convdate} loading={isPending}>
          Confirm
        </Button>
      </Actions>
    </StandardModal>
  );
}
